import { Inject, Injectable } from '@angular/core';
import { Router, ROUTER_CONFIGURATION } from '@angular/router';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { ListRegistersResponse } from '../models/list-registers-response';
import { Register } from '../models/register';
import { AppNavigationService } from '../services/app-navigation.service';
import { OrganisationService } from '../services/organisation.service';
import {
  navigateToRouteWithReplacedParameters,
  TypedActivatedRouteSnapshot,
} from '../utils/route-utils';
import { OrganisationData, OrganisationParams } from './organisation.resolver';

export type RegisterParams = OrganisationParams & {
  registerId: string;
};

export type RegisterData = OrganisationData & {
  register: Register;
};

// Todo: Fetch permissions from API and store them in storage
@Injectable({
  providedIn: 'root',
})
export class RegisterResolver {
  private static _registers$:
    | (Observable<ListRegistersResponse> & { organisationId?: number })
    | undefined;

  public static clearCache() {
    RegisterResolver._registers$ = undefined;
  }

  private listRegisters(organisationId: number) {
    if (
      !RegisterResolver._registers$ ||
      RegisterResolver._registers$.organisationId !== organisationId
    ) {
      RegisterResolver._registers$ = this.organisationService
        .listRegisters(organisationId)
        .pipe(shareReplay());
    }

    RegisterResolver._registers$.organisationId = organisationId;
    return RegisterResolver._registers$;
  }

  constructor(
    private router: Router,
    private organisationService: OrganisationService,
    private appNavigationService: AppNavigationService,
    @Inject(ROUTER_CONFIGURATION) private routerConfig: { enableTracing: boolean },
  ) {}

  resolve(route: TypedActivatedRouteSnapshot<RegisterParams>): Observable<Register> {
    const organisationId = +route.params.organisationId;

    if (!organisationId) {
      throw new Error('No organisationId param');
    }

    const registerPath = route.params.registerId || '';
    let registerId = +registerPath.split('-')[0];

    return this.listRegisters(organisationId).pipe(
      map(({ registers }) => {
        let register = registerPath !== '-' ? registers.find(r => r.id === registerId) : null;

        if (register) {
          if (this.routerConfig.enableTracing) {
            console.log('Register Resolver: resolved', register);
          }

          this.appNavigationService.switchRegister(register, true);

          sessionStorage.setItem('registerId', registerId.toString());
          localStorage.setItem('registerId', registerId.toString());
        } else if (registerPath === '-') {
          // Only redirect if a registerId hasn't been set (=== '-')
          // Try to get registerId from session
          registerId = +(sessionStorage.getItem('registerId') || '');
          register = registers.find(r => r.id === registerId);

          if (!register) {
            // Else try to get registerId from local storage
            // Else fall back to default
            registerId = +(localStorage.getItem('registerId') || '');
            register = registers.find(r => r.id === registerId) || registers[0];
          }

          if (register) {
            if (this.routerConfig.enableTracing) {
              console.log('Register Resolver: redirecting', register);
            }
            navigateToRouteWithReplacedParameters(
              this.router,
              {
                registerId: register.id,
              },
              route,
            );
          }
        }

        // No register, redirect to root
        if (!register) {
          if (this.routerConfig.enableTracing) {
            console.warn('Register Resolver: no register found', registers);
          }

          this.router.navigateByUrl('/');
          return null as never;
        }

        return register;
      }),
    );
  }
}
